/**
 * 업그레이드 시스템 (50개)
 * - 생산기 업그레이드 36개 (12생산기 × 3단계): 해당 생산기 CPS ×2
 * - 글로벌 업그레이드 8개: 전체 효과
 * - 연구 업그레이드 6개: 고급 부스트
 */

export interface UpgradeConfig {
  id: string;
  name: string;
  description: string;
  category: 'generator' | 'global' | 'research' | 'darkpool';
  cost: number;
  /** 해금 조건 */
  unlock: {
    type: 'generatorCount' | 'totalEarned' | 'prestigeCount' | 'cps' | 'always' | 'darkpoolBets';
    generatorId?: string;
    value: number;
  };
  /** 효과 */
  effect: {
    type: 'generatorMultiplier' | 'globalCPS' | 'clickPower' | 'goldenChance' | 'goldenDuration' | 'offlineRate' | 'comboDecay' | 'comboMax' | 'dpRewardMult' | 'dpInsiderAccuracy' | 'dpMaxBet' | 'dpHighMultChance';
    generatorId?: string;
    value: number; // multiplier or additive %
  };
  icon: string;
}

// ===== 생산기 업그레이드 (12 × 3 = 36개) =====
const GEN_NAMES = [
  { id: 'gen1', name: '스마트폰', icon: '', costs: [100, 5000, 500000] },
  { id: 'gen2', name: '노트북', icon: '', costs: [1000, 50000, 5e6] },
  { id: 'gen3', name: '데스크탑', icon: '', costs: [11000, 5.5e5, 5.5e7] },
  { id: 'gen4', name: 'GPU', icon: '', costs: [120000, 6e6, 6e8] },
  { id: 'gen5', name: 'ASIC', icon: '', costs: [1.3e6, 6.5e7, 6.5e9] },
  { id: 'gen6', name: '농장', icon: '', costs: [14e6, 7e8, 7e10] },
  { id: 'gen7', name: '데이터센터', icon: '', costs: [2e8, 1e10, 1e12] },
  { id: 'gen8', name: '양자컴퓨터', icon: '', costs: [3.3e9, 1.65e11, 1.65e13] },
  { id: 'gen9', name: 'AI봇', icon: '', costs: [5.1e10, 2.55e12, 2.55e14] },
  { id: 'gen10', name: '위성', icon: '', costs: [7.5e11, 3.75e13, 3.75e15] },
  { id: 'gen11', name: '달기지', icon: '', costs: [1e13, 5e14, 5e16] },
  { id: 'gen12', name: '은하', icon: '', costs: [1.4e14, 7e15, 7e17] },
];

const TIER_NAMES = ['효율화', '특화', '마스터'];
const TIER_UNLOCKS = [1, 25, 100]; // 해당 생산기 보유 수

const generatorUpgrades: UpgradeConfig[] = [];
for (const gen of GEN_NAMES) {
  for (let tier = 0; tier < 3; tier++) {
    generatorUpgrades.push({
      id: `up_${gen.id}_t${tier + 1}`,
      name: `${gen.name} ${TIER_NAMES[tier]}`,
      description: `${gen.name} 채굴 효율 2배`,
      category: 'generator',
      cost: gen.costs[tier],
      unlock: {
        type: 'generatorCount',
        generatorId: gen.id,
        value: TIER_UNLOCKS[tier],
      },
      effect: {
        type: 'generatorMultiplier',
        generatorId: gen.id,
        value: 2,
      },
      icon: gen.icon,
    });
  }
}

// ===== 글로벌 업그레이드 (8개) =====
const globalUpgrades: UpgradeConfig[] = [
  {
    id: 'up_global_click1',
    name: '강화 손가락',
    description: '탭 파워 ×2',
    category: 'global',
    cost: 500,
    unlock: { type: 'totalEarned', value: 1000 },
    effect: { type: 'clickPower', value: 2 },
    icon: '',
  },
  {
    id: 'up_global_click2',
    name: '사이보그 손가락',
    description: '탭 파워 ×3',
    category: 'global',
    cost: 5e5,
    unlock: { type: 'totalEarned', value: 1e6 },
    effect: { type: 'clickPower', value: 3 },
    icon: '',
  },
  {
    id: 'up_global_cps1',
    name: '오버클럭',
    description: '전체 CPS +50%',
    category: 'global',
    cost: 5e4,
    unlock: { type: 'totalEarned', value: 1e5 },
    effect: { type: 'globalCPS', value: 1.5 },
    icon: '',
  },
  {
    id: 'up_global_cps2',
    name: '극한 오버클럭',
    description: '전체 CPS ×2',
    category: 'global',
    cost: 5e8,
    unlock: { type: 'totalEarned', value: 1e9 },
    effect: { type: 'globalCPS', value: 2 },
    icon: '',
  },
  {
    id: 'up_global_golden1',
    name: '행운의 안테나',
    description: '골든 이벤트 지속시간 +50%',
    category: 'global',
    cost: 7.77e5,
    unlock: { type: 'totalEarned', value: 5e5 },
    effect: { type: 'goldenDuration', value: 1.5 },
    icon: '',
  },
  {
    id: 'up_global_golden2',
    name: '골든 자석',
    description: '골든 이벤트 출현 빈도 2배',
    category: 'global',
    cost: 7.77e8,
    unlock: { type: 'totalEarned', value: 5e8 },
    effect: { type: 'goldenChance', value: 2 },
    icon: '',
  },
  {
    id: 'up_global_offline',
    name: '자동 채굴 최적화',
    description: '오프라인 수익률 50% → 75%',
    category: 'global',
    cost: 1e7,
    unlock: { type: 'totalEarned', value: 1e7 },
    effect: { type: 'offlineRate', value: 0.25 },
    icon: '',
  },
  {
    id: 'up_global_combo',
    name: '콤보 지속 강화',
    description: '콤보 유지 시간 3초 → 5초',
    category: 'global',
    cost: 2.5e4,
    unlock: { type: 'totalEarned', value: 5e4 },
    effect: { type: 'comboDecay', value: 5000 },
    icon: '⏱',
  },
];

// ===== 연구 업그레이드 (6개) =====
const researchUpgrades: UpgradeConfig[] = [
  {
    id: 'up_res_synergy',
    name: '시너지 알고리즘',
    description: '보유 생산기 종류당 CPS +10%',
    category: 'research',
    cost: 1e10,
    unlock: { type: 'totalEarned', value: 1e10 },
    effect: { type: 'globalCPS', value: 1.1 }, // 종류당이라 코드에서 특수 처리
    icon: '',
  },
  {
    id: 'up_res_quantum',
    name: '양자 가속',
    description: '전체 CPS ×3',
    category: 'research',
    cost: 1e13,
    unlock: { type: 'totalEarned', value: 5e12 },
    effect: { type: 'globalCPS', value: 3 },
    icon: '',
  },
  {
    id: 'up_res_ai_click',
    name: 'AI 클릭 보조',
    description: '탭 파워 ×5',
    category: 'research',
    cost: 5e11,
    unlock: { type: 'totalEarned', value: 1e11 },
    effect: { type: 'clickPower', value: 5 },
    icon: '',
  },
  {
    id: 'up_res_golden_master',
    name: '골든 마스터',
    description: '골든 이벤트 효과 2배 + 지속시간 2배',
    category: 'research',
    cost: 5e12,
    unlock: { type: 'totalEarned', value: 1e12 },
    effect: { type: 'goldenDuration', value: 2 },
    icon: '',
  },
  {
    id: 'up_res_prestige_boost',
    name: '환생 촉진제',
    description: '환생 시 블록체인 +50% 추가 획득',
    category: 'research',
    cost: 1e9,
    unlock: { type: 'prestigeCount', value: 3 },
    effect: { type: 'globalCPS', value: 1 }, // 환생 계산에서 특수 처리
    icon: '',
  },
  {
    id: 'up_res_combo_max',
    name: '콤보 브레이커',
    description: '최대 콤보 배율 2배 → 3배',
    category: 'research',
    cost: 5e6,
    unlock: { type: 'totalEarned', value: 5e6 },
    effect: { type: 'comboMax', value: 3 },
    icon: '',
  },
];

// ===== 다크풀 업그레이드 (4개) =====
const darkpoolUpgrades: UpgradeConfig[] = [
  {
    id: 'up_dp_scam_promo',
    name: '스캠 코인 홍보',
    description: '다크풀 보상 x2',
    category: 'darkpool',
    cost: 5e7,
    unlock: { type: 'darkpoolBets', value: 5 },
    effect: { type: 'dpRewardMult', value: 2 },
    icon: '',
  },
  {
    id: 'up_dp_insider_net',
    name: '인사이더 네트워크',
    description: '내부자 정보 정확도 80% → 95%',
    category: 'darkpool',
    cost: 1e8,
    unlock: { type: 'darkpoolBets', value: 15 },
    effect: { type: 'dpInsiderAccuracy', value: 0.95 },
    icon: '',
  },
  {
    id: 'up_dp_chip_laundry',
    name: '고액 베팅',
    description: '최대 베팅 25% → 50%',
    category: 'darkpool',
    cost: 2e8,
    unlock: { type: 'darkpoolBets', value: 30 },
    effect: { type: 'dpMaxBet', value: 0.5 },
    icon: '',
  },
  {
    id: 'up_dp_whale_ctrl',
    name: '고래 조종',
    description: '8x, 12x 배율 확률 대폭 증가',
    category: 'darkpool',
    cost: 5e8,
    unlock: { type: 'darkpoolBets', value: 50 },
    effect: { type: 'dpHighMultChance', value: 1 },
    icon: '',
  },
];

export const UPGRADES: UpgradeConfig[] = [
  ...generatorUpgrades,
  ...globalUpgrades,
  ...researchUpgrades,
  ...darkpoolUpgrades,
];

/**
 * 생산기 업그레이드 배율 계산
 */
export function getUpgradeMultiplier(generatorId: string, purchasedUpgrades: string[]): number {
  let mult = 1;
  for (const up of UPGRADES) {
    if (
      up.effect.type === 'generatorMultiplier' &&
      up.effect.generatorId === generatorId &&
      purchasedUpgrades.includes(up.id)
    ) {
      mult *= up.effect.value;
    }
  }
  return mult;
}

/**
 * 글로벌 CPS 배율 (업그레이드에서)
 */
export function getGlobalCPSMultiplier(purchasedUpgrades: string[], ownedGeneratorTypes: number): number {
  let mult = 1;
  for (const up of UPGRADES) {
    if (!purchasedUpgrades.includes(up.id)) continue;
    if (up.effect.type === 'globalCPS') {
      if (up.id === 'up_res_synergy') {
        mult *= Math.pow(up.effect.value, ownedGeneratorTypes);
      } else {
        mult *= up.effect.value;
      }
    }
  }
  return mult;
}

/**
 * 글로벌 클릭파워 배율 (업그레이드에서)
 */
export function getClickPowerMultiplier(purchasedUpgrades: string[]): number {
  let mult = 1;
  for (const up of UPGRADES) {
    if (!purchasedUpgrades.includes(up.id)) continue;
    if (up.effect.type === 'clickPower') {
      mult *= up.effect.value;
    }
  }
  return mult;
}

/**
 * 업그레이드 해금 여부 확인
 */
export function isUpgradeUnlocked(
  up: UpgradeConfig,
  state: {
    generators: Record<string, { count: number }>;
    statistics: { totalEarned: number };
    prestige: { count: number };
    darkPool?: { totalBets: number };
  }
): boolean {
  switch (up.unlock.type) {
    case 'always': return true;
    case 'generatorCount': {
      const gen = state.generators[up.unlock.generatorId!];
      return gen ? gen.count >= up.unlock.value : false;
    }
    case 'totalEarned': return state.statistics.totalEarned >= up.unlock.value;
    case 'prestigeCount': return state.prestige.count >= up.unlock.value;
    case 'darkpoolBets': return (state.darkPool?.totalBets || 0) >= up.unlock.value;
    default: return false;
  }
}
